/**
 * Session manager for managing session state across environments
 * Handles both cookie-based and localStorage-based session management
 * for maximum compatibility with different deployment scenarios
 */

// Store session ID in localStorage
const SESSION_STORAGE_KEY = "app_session_id";

/**
 * Save session ID to localStorage and optionally as a cookie
 */
export function saveSessionId(sessionId: string): void {
  if (typeof window !== "undefined") {
    // Always save to localStorage
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    console.log("📥 Session ID saved to localStorage:", sessionId);

    // Also try to set as a cookie for environments where that works better
    try {
      // Get the domain from the current URL
      const currentDomain = window.location.hostname;

      // Detect environment
      const isLocalDevelopment = currentDomain === "localhost";
      const isProductionDomain = currentDomain.includes("evefant.com");

      // Set cookie with appropriate options for the current environment
      const cookieOptions = [
        `sessionId=${sessionId}`,
        "path=/",
        `max-age=${30 * 24 * 60 * 60}`,
      ];

      // Configure SameSite based on environment
      if (isLocalDevelopment) {
        cookieOptions.push("SameSite=Lax");
      } else {
        cookieOptions.push("SameSite=None");
      }

      // Add Secure flag for production or when using HTTPS
      if (isProductionDomain || window.location.protocol === "https:") {
        cookieOptions.push("Secure");
      }

      // Add domain for production environment
      if (isProductionDomain) {
        cookieOptions.push("domain=.evefant.com"); // Note the dot prefix for subdomains
      }

      // Set the cookie with all options
      document.cookie = cookieOptions.join("; ");

      console.log(
        "📥 Session ID saved as cookie with options:",
        cookieOptions.join("; ")
      );
    } catch (e) {
      console.warn("⚠️ Could not save session ID as cookie:", e);
    }

    // Add visual indicator for testing in development
    if (process.env.NODE_ENV !== "production") {
      console.log(
        "%c 🔑 Session ID: " + sessionId,
        "background: #333; color: #bada55; padding: 5px;"
      );
    }
  }
}

/**
 * Get saved session ID from cookie or localStorage
 * Prioritizes cookies (backend-generated) but falls back to localStorage if needed
 */
export function getSessionId(): string | null {
  if (typeof window !== "undefined") {
    // First try to get from cookie (backend-generated)
    try {
      const cookies = document.cookie.split(";");
      const sessionCookie = cookies.find((cookie) =>
        cookie.trim().startsWith("sessionId=")
      );

      if (sessionCookie) {
        const sessionIdFromCookie = sessionCookie.split("=")[1];
        if (sessionIdFromCookie) {
          console.log("📥 Retrieved session ID from cookie");
          // Save to localStorage for future use
          localStorage.setItem(SESSION_STORAGE_KEY, sessionIdFromCookie);
          return sessionIdFromCookie;
        }
      }
    } catch (e) {
      console.warn("⚠️ Error retrieving session ID from cookie:", e);
    }

    // Fallback to localStorage if no cookie found
    const localStorageSessionId = localStorage.getItem(SESSION_STORAGE_KEY);
    if (localStorageSessionId) {
      console.log("📥 Retrieved session ID from localStorage");
      return localStorageSessionId;
    }
  }
  return null;
}

/**
 * Clear saved session ID from all storage mechanisms
 */
export function clearSessionId(): void {
  if (typeof window !== "undefined") {
    // Clear from localStorage
    localStorage.removeItem(SESSION_STORAGE_KEY);

    // Clear from cookie
    try {
      document.cookie =
        "sessionId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    } catch (e) {
      console.warn("⚠️ Error clearing session ID cookie:", e);
    }

    console.log("🧹 Session ID cleared from all storage");
  }
}

/**
 * Debug function to show current session status in console
 * Useful for testing with friends and family
 * @returns Object containing sessionId and cartId
 */
export function debugSession(): {
  sessionId: string | null;
  cartId: string | null;
} {
  if (typeof window !== "undefined") {
    const sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
    const cartId = localStorage.getItem("cartId");

    console.group("📊 Session Debug Info");
    console.log("Session ID:", sessionId || "Not set");
    console.log("Cart ID:", cartId || "Not set");
    console.log(
      "To share your test session, send these values to the developer"
    );
    console.groupEnd();

    return { sessionId, cartId };
  }
  return { sessionId: null, cartId: null };
}
